/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ChevronDown } from '@carbon/react/icons';
import { noopFn } from '../../../../Utilities/propHelpers/defaultProps.js';
import NavItem from './NavItem.js';
import { findActiveItem as _findActiveItem, isActiveItem } from '../utils/findActiveItem.js';
import NavFlyout from './NavFlyout.js';

var _excluded = ["active", "activeHref", "className", "id", "items", "label", "menuButtonComponent", "onClick", "onItemSelect", "open", "linkComponent", "icon"],
  _excluded2 = ["onClick", "onKeyDown", "href", "to", "label", "flyoutItems"];
var NavMenu = function NavMenu(_ref) {
  var navMenuActive = _ref.active,
    activeHref = _ref.activeHref,
    className = _ref.className,
    id = _ref.id,
    items = _ref.items,
    menuButtonLabel = _ref.label,
    _ref$menuButtonCompon = _ref.menuButtonComponent,
    MenuButtonComponent = _ref$menuButtonCompon === void 0 ? 'button' : _ref$menuButtonCompon,
    _ref$onClick = _ref.onClick,
    onClick = _ref$onClick === void 0 ? noopFn : _ref$onClick,
    onItemSelect = _ref.onItemSelect,
    controlledOpen = _ref.open,
    _ref$linkComponent = _ref.linkComponent,
    LinkComponent = _ref$linkComponent === void 0 ? 'a' : _ref$linkComponent,
    Icon = _ref.icon,
    rest = _objectWithoutProperties(_ref, _excluded);
  // Component State
  var _useState = useState(controlledOpen || navMenuActive),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var iconItem = !Icon ? null : /*#__PURE__*/React.createElement(Icon, {
    "aria-label": "icon",
    className: "cpc--side-nav__item-icon",
    "aria-hidden": "true"
  });

  // Event Handlers
  var toggleOpen = function toggleOpen() {
    setOpen(!open);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    onClick.apply(void 0, [!open].concat(args));
  };
  useEffect(function () {
    // only use controlledOpen if it's explicitly set
    if (typeof controlledOpen === 'boolean') {
      setOpen(controlledOpen);
    }
  }, [controlledOpen]);
  return /*#__PURE__*/React.createElement("li", _extends({
    className: classnames('cpc--side-nav__item', 'cpc--side-nav__item--with-menu', {
      'cpc--side-nav__item--active': navMenuActive && !open
    }, className)
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, rest), /*#__PURE__*/React.createElement(MenuButtonComponent, {
    "aria-haspopup": "true",
    "aria-expanded": open,
    type: "button",
    className: "cpc--side-nav__menu-button",
    id: id,
    onClick: toggleOpen
  }, /*#__PURE__*/React.createElement("div", {
    className: "cpc--side-nav__menu-button__label"
  }, iconItem, menuButtonLabel), /*#__PURE__*/React.createElement(ChevronDown, {
    className: "cpc--side-nav__menu-icon"
  })), /*#__PURE__*/React.createElement("ul", {
    className: "cpc--side-nav__menu"
  }, items.map(function (item) {
    var onItemClick = item.onClick,
      onItemKeyDown = item.onKeyDown,
      href = item.href,
      to = item.to,
      label = item.label,
      flyoutItems = item.flyoutItems,
      props = _objectWithoutProperties(item, _excluded2);
    if (flyoutItems) {
      return /*#__PURE__*/React.createElement(NavFlyout, _extends({
        key: "".concat(label, "-").concat(href),
        active: !!_findActiveItem(flyoutItems, activeHref),
        label: label,
        activeHref: activeHref,
        items: flyoutItems,
        onItemSelect: onItemSelect,
        linkComponent: LinkComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
      }, props));
    }
    return /*#__PURE__*/React.createElement(NavItem, _extends({
      key: "".concat(href, "-").concat(label),
      active: isActiveItem(item, activeHref),
      linkComponent: href ? 'a' : LinkComponent,
      label: label,
      to: to,
      href: href,
      onClick: function onClick(evt) {
        return onItemSelect(evt, to || href, onItemClick);
      },
      onKeyDown: function onKeyDown(evt) {
        return onItemSelect(evt, to || href, onItemKeyDown);
      }
      // eslint-disable-next-line react/jsx-props-no-spreading
    }, props));
  })));
};
NavMenu.propTypes = {
  /**
   * Whether the nav menu is active or not.
   */
  active: PropTypes.bool.isRequired,
  /**
   * The href of the nav item that should be active.
   */
  activeHref: PropTypes.string.isRequired,
  /**
   * Any class names to apply to the nav list element.
   */
  className: PropTypes.string,
  /**
   * A unique identifier to apply to the nav list element.
   */
  id: PropTypes.string,
  /**
   * Programmatically control opening/closing of NavMenu. Only booleans will be respected.
   */
  open: PropTypes.bool,
  /**
   * The label to assign to the nav list.
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * The component to use for link elements. Useful for when using React Router.
   */
  linkComponent: PropTypes.elementType,
  /**
   * A menu button component to render instead of the button. Useful if teams need to intercept
   * the menu button to render a separate component in its place, such as when nesting including
   * interactive content within the menu.
   */
  menuButtonComponent: PropTypes.elementType,
  /**
   * The onClick event listener to bind to the nav button.
   */
  onClick: PropTypes.func,
  /**
   * The event to fire off when selecting a navigation item.
   */
  onItemSelect: PropTypes.func.isRequired,
  /**
   * The items to pass in for the nav list to render.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    href: PropTypes.string,
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      href: PropTypes.string,
      label: PropTypes.string
    }))
  })).isRequired,
  /**
   * Icon of the item
   */
  icon: NavItem.propTypes.icon
};
var NavMenu$1 = NavMenu;

export { NavMenu$1 as default };
